import React, { useEffect } from 'react';
import './CardsList.css';
import { getBrandLogoFilename } from '../brandMapping';
import { getProductIcon } from '../productIconMapping';
import { formatAandeelDisplay } from '../utils/aandeelDisplay';

const CardsList = ({ filteredData, selectedRow, setSelectedRow, getDisplayColumns }) => {
  // Function to get brand logo filename based on title
  const getBrandLogo = (title) => {
    // console.log('🎨 getBrandLogo called with title:', title);
    
    if (!title) return 'brand-placeholder.svg';
    
    // First check if we have a mapping for this brand
    const mappedLogo = getBrandLogoFilename(title);
    if (mappedLogo) {
      // console.log('✅ Using mapped logo:', mappedLogo);
      return mappedLogo;
    }
    
    // console.log('❌ No mapping found, using fallback logic');
    
    // If no mapping found, fall back to the original logic
    // Remove .nl from the title first
    const titleWithoutDomain = title.replace(/\.nl/gi, '');
    
    // Clean up the title to create a potential filename
    // Convert to lowercase and remove special characters
    const cleanTitle = titleWithoutDomain.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')  // Remove special characters
      .trim()
      .replace(/\s+/g, ' ');        // Normalize spaces
    
    // console.log('🧹 Clean title:', cleanTitle);
    
    // Try different variations of the title as potential filenames
    const potentialNames = [
      cleanTitle,                                // Full cleaned title with spaces
      cleanTitle.split(' ')[0],                  // First word
      cleanTitle.replace(/\s+/g, '')             // No spaces
    ];
    
    // console.log('📋 Potential names:', potentialNames);
    
    // Return the first potential name + .svg extension
    // The onError handler in the img tag will handle fallback if file doesn't exist
    const finalName = `${potentialNames[0]}.svg`;
    // console.log('🎯 Final filename:', finalName);
    return finalName;
  };
  
  // Function to handle card selection and scroll to product details
  const handleCardSelect = (row) => {
    // Set the selected row
    setSelectedRow(row);
    
    // Check if we're on mobile (screen width <= 768px)
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // On mobile, scroll to the detailed-view element
      setTimeout(() => {
        const detailedView = document.querySelector('.detailed-view');
        if (detailedView) {
          detailedView.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // On desktop, scroll to the product details section (existing behavior)
      setTimeout(() => {
        const detailsSection = document.getElementById('product-details-section');
        if (detailsSection) {
          detailsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  // Function to properly parse Dutch and dot-decimal formatted currency
  const parseDutchCurrency = (currencyStr) => {
    if (!currencyStr) return 0;
    const s = currencyStr.toString().replace(/€/g, '').trim();
    // If it has a comma, treat as Dutch decimal: 1.234,56 or 12,5
    if (s.includes(',')) {
      return parseFloat(s.replace(/\./g, '').replace(/,/g, '.')) || 0;
    }
    // If no comma and looks like a dot-decimal number (e.g., 12.5), parse directly
    const dotCount = (s.match(/\./g) || []).length;
    if (dotCount === 1 && /^\d+\.\d+$/.test(s)) {
      return parseFloat(s) || 0;
    }
    // Otherwise treat dots as thousands and strip them
    return parseFloat(s.replace(/\./g, '')) || 0;
  };

  // Format price with comma as decimal separator and dot as thousands separator (Dutch format)
  const formatPrice = (price) => {
    if (price === null || isNaN(price)) return '0,00';
    
    // Format with 2 decimal places and replace decimal point with comma for Dutch format
    let formattedPrice = price.toFixed(2).replace('.', ',');
    
    // Add thousands separator (dot) for Dutch format
    if (price >= 1000) {
      const parts = formattedPrice.split(',');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      formattedPrice = parts.join(',');
    }
    
    return formattedPrice;
  };

  // Function to calculate the total price (Basisprijs x Positiefactor x Formaatfactor)
  const calculateTotalPrice = (row) => {
    if (!row['Basisprijs']) return row['Basisprijs'] || '-';
    
    // Apply calculation for both Print and Online media
    const isPrint = row.Medium === 'Print';
    const isOnline = ['Online', 'Display', 'Video'].includes(row.Mediumtype) ||
      ['Online', 'Display', 'Video'].includes(row.Medium);
    const basisPrice = parseDutchCurrency(row['Basisprijs']);
    // Skip calculation if not Print or Online
    if (!isPrint && !isOnline) return `€ ${formatPrice(basisPrice)}`;
    
    // Parse the base price from Dutch currency format
    const basePrice = parseDutchCurrency(row['Basisprijs']);
    if (isNaN(basePrice)) return row['Basisprijs'];
    
    // Get the position factor and format factor, default to 1 if not present
    const positionFactor = parseDutchCurrency(row['Positiefactor']) || 1;
    const formatFactor = parseDutchCurrency(row['Formaatfactor']) || 1;
    
    // Calculate the total price
    const totalPrice = basePrice * positionFactor * formatFactor;
    
    // Format and return the price
    return `€ ${formatPrice(totalPrice)}`;
  };

  return (
    <div className="cards-list">
      {filteredData.map((row, rowIndex) => {
        const brandLogo = getBrandLogo(row['Titel']);
        const { iconPath, isPrint, isDigital, placeholderPath } = getProductIcon(row);
        const totalPrice = calculateTotalPrice(row);
        
        return (
          <div 
            key={rowIndex}
            onClick={() => handleCardSelect(row)}
            className={`card ${selectedRow === row ? 'selected' : ''}`}
          >
            <div className="card-badge">
              {row.Mediumtype === 'Nieuwsblad' || row.Mediumtype === 'Weekblad'
                ? 'Nieuws- en weekbladen'
                : (row.Mediumtype && row.Mediumtype.startsWith('Dagblad'))
                  ? 'Dagblad'
                  : row.Mediumtype}
            </div>

            <div className="card-content">
              <div className="card-two-columns">
                {/* Left column - title and all fields except Basisprijs */}
                <div className="card-left-column">
                  {/* Brand logo container - always rendered to preserve height */}
                  <div className="card-brand-logo" data-img={brandLogo}>
                    <img 
                      src={`${process.env.PUBLIC_URL}/assets/brands/${brandLogo}`} 
                      alt={`${row['Titel']} logo`}
                      onError={(e) => {
                        // If this is the first error, try the first word of the title
                        if (!e.target.dataset.tried) {
                          e.target.dataset.tried = '1';
                          const titleWithoutDomain = row['Titel']?.replace(/\.nl/gi, '');
                          const firstWord = titleWithoutDomain?.toLowerCase().split(' ')[0];
                          if (firstWord) {
                            e.target.src = `${process.env.PUBLIC_URL}/assets/brands/${firstWord}.svg`;
                            e.target.parentElement.setAttribute('data-img', `${firstWord}.svg`);
                            return;
                          }
                        }
                        // If this is the second error, try the title without spaces
                        else if (e.target.dataset.tried === '1') {
                          e.target.dataset.tried = '2';
                          const titleWithoutDomain = row['Titel']?.replace(/\.nl/gi, '');
                          const noSpaces = titleWithoutDomain?.toLowerCase().replace(/[^a-z0-9]/g, '');
                          if (noSpaces) {
                            e.target.src = `${process.env.PUBLIC_URL}/assets/brands/${noSpaces}.svg`;
                            e.target.parentElement.setAttribute('data-img', `${noSpaces}.svg`);
                            return;
                          }
                        }
                        
                        // If all attempts fail, use the placeholder
                        e.target.onerror = null;
                        e.target.src = `${process.env.PUBLIC_URL}/assets/brands/brand-placeholder.svg`;
                        e.target.parentElement.setAttribute('data-img', 'brand-placeholder.svg');
                      }}
                    />
                  </div>
                  
                  {/* Title in the left column */}
                  {getDisplayColumns().includes('Titel') && (
                    <div className="card-field title-field">
                      <div className="card-field-value title">
                        {row['Titel'] || '-'}
                      </div>
                    </div>
                  )}
                  
                  {/* Plaatsingsdag field for Print items */}
                  {isPrint && row['Plaatsingsdag'] && (
                    <div className="card-field">
                      <div className="card-field-value small">
                        <strong>Plaatsingsdag: </strong>{row['Plaatsingsdag']}
                      </div>
                    </div>
                  )}
                  
                  {/* Show Aandeel for Print media, Formaat for others */}
                  {isPrint && row['Aandeel'] && (
                    <div className="card-field">
                      <div className="card-field-value small">
                        <strong>Aandeel: </strong>{formatAandeelDisplay(row['Aandeel'])}
                      </div>
                    </div>
                  )}
                  {!isPrint && row['Formaat'] && (
                    <div className="card-field">
                      <div className="card-field-value small">
                        <strong>Formaat: </strong>{row['Formaat']}
                      </div>
                    </div>
                  )}
                  
                  {getDisplayColumns()
                    .filter(column => column !== 'Titel' && column !== 'Basisprijs' && 
                           (isPrint ? column !== 'Aandeel' : column !== 'Formaat'))
                    .map((column, colIndex) => (
                      <div key={colIndex} className="card-field">
                        <div className="card-field-value small">
                          {row[column] || '-'}
                        </div>
                      </div>
                    ))}
                </div>
                
                {/* Right column - Basisprijs and Product Icon */}
                {getDisplayColumns().includes('Basisprijs') && (
                  <div className="card-right-column">
                    {/* Product icon (for either print or digital) */}
                    {(isPrint || isDigital) && (
                      <div className={`card-product-icon ${isPrint ? 'print-icon' : ''} ${isDigital ? 'digital-icon' : ''}`}>
                        <img 
                          src={`${process.env.PUBLIC_URL}${iconPath}`} 
                          alt={isPrint ? `${row['Formaat']} size` : `${row['Product'] || row['Formaat']} product`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `${process.env.PUBLIC_URL}${placeholderPath}`;
                          }}
                        />
                      </div>
                    )}
                    <div className="card-field-value price">
                      {totalPrice}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CardsList; 